// React imports
import React, { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

// External library imports
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import axios from "axios";

// Local imports
import {
  ToastVariant,
  useCustomToast,
} from "../../../components/Methods/SnackBar";
import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  postData,
  putData,
} from "../../../store/actions";
import { createKRAUrl, updateKRAUrl } from "../../../Service/Apis";
import { KRASection, KRAFormData, KRATemplateData } from "./Types/KRATypes";
import { AxiosErrorResponse } from "./Types/ErrorTypes";

// Helper functions (outside component - don't depend on component state)
const getCurrentQuarter = (): string => {
  const month = new Date().getMonth() + 1;
  const year = new Date().getFullYear();
  const quarter = Math.ceil(month / 3);
  return `Q${quarter} ${year}`;
};

const transformTemplateSections = (sections: KRATemplateData['sections']): KRASection[] => {
  return sections.map((section) => ({
    section: section.section,
    weightage: section.weightage,
    questions: section.questions.map((q) => ({
      question: q.question,
      weightage: q.weightage,
      rating_type: q.rating_type || 'numerical',
    })),
  }));
};

const CreateKRAForm: React.FC = () => {
  // 1. useState declarations
  const params = useParams<{ designationId?: string; id?: string }>();
  const location = useLocation();
  
  // Try to get designationId from URL params first, then fallback to location state
  const designationId = params.designationId || location.state?.designationId;
  const templateId = params.id;  // For edit mode, the template ID comes from :id param
  const designationName = location.state?.designationName || "Unknown Designation";
  const templateData = location.state?.templateData as KRATemplateData | undefined;
  const isEditMode = !!templateId && !!templateData;

  const initialState: KRAFormData = isEditMode && templateData
    ? {
        form_name: templateData.form_name,
        sections: transformTemplateSections(templateData.sections),
      }
    : {
        form_name: `${designationName} - ${getCurrentQuarter()}`,
        sections: [
          {
            section: "",
            weightage: 0,
            questions: [
              {
                question: "",
                weightage: 0,
                rating_type: 'numerical',
              },
            ],
          },
        ],
      };

  // useState declarations
  const [formData, setFormData] = useState<KRAFormData>(initialState);
  const [reviewPeriod, setReviewPeriod] = useState<string>(
    isEditMode && templateData?.review_period ? templateData.review_period : 'quarterly'
  );
  const [initialReviewPeriod] = useState<string>(
    isEditMode && templateData?.review_period ? templateData.review_period : 'quarterly'
  );
  const [enable360Feedback, setEnable360Feedback] = useState<boolean>(
    isEditMode && templateData?.enable_360_feedback ? templateData.enable_360_feedback : false
  );
  const [deleteSectionIndex, setDeleteSectionIndex] = useState<number | null>(null);
  const [deleteQuestionIndex, setDeleteQuestionIndex] = useState<{
    sectionIndex: number | null;
    questionIndex: number | null;
  }>({ sectionIndex: null, questionIndex: null });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // 2. const variables (hooks, Redux, computed constants)
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  // 3. Function declarations
  const handleShowToast = (
    message: string,
    variant: ToastVariant,
    isCloseable: boolean
  ): void => {
    showToast(message, variant, isCloseable);
  };

  // Calculate total KRA weightage
  const calculateTotalWeightage = (): number => {
    return formData.sections.reduce(
      (total, section) => total + (section.weightage || 0),
      0
    );
  };

  // Calculate total KPI weightage within a section
  const calculateSectionKPIWeightage = (sectionIndex: number): number => {
    return formData.sections[sectionIndex].questions.reduce(
      (total, question) => total + (question.weightage || 0),
      0
    );
  };

  const addSection = (): void => {
    const newSection: KRASection = {
      section: "",
      weightage: 0,
      questions: [
        {
          question: "",
          weightage: 0,
          rating_type: 'numerical',
        },
      ],
    };
    setFormData({
      ...formData,
      sections: [...formData.sections, newSection],
    });
  };

  const addQuestion = (sectionIndex: number): void => {
    const updatedFormData = { ...formData };
    updatedFormData.sections[sectionIndex].questions.push({
      question: "",
      weightage: 0,
      rating_type: 'numerical',
    });
    setFormData(updatedFormData);
  };

  const deleteSection = (sectionIndex: number): void => {
    const totalSections = formData.sections.length;
    if (sectionIndex > -1 && totalSections > 1) {
      const updatedFormData = { ...formData };
      updatedFormData.sections.splice(sectionIndex, 1);
      setFormData(updatedFormData);
      setDeleteSectionIndex(null);
    } else if (totalSections <= 1) {
      handleShowToast("Cannot Delete Last KRA.", "info", true);
      setDeleteSectionIndex(null);
    }
  };

  const deleteQuestion = (sectionIndex: number, questionIndex: number): void => {
    const totalQuestions = formData.sections[sectionIndex].questions.length;
    if (questionIndex > -1 && totalQuestions > 1) {
      const updatedFormData = { ...formData };
      updatedFormData.sections[sectionIndex].questions.splice(questionIndex, 1);
      setFormData(updatedFormData);
      setDeleteQuestionIndex({ sectionIndex: null, questionIndex: null });
    } else if (totalQuestions <= 1) {
      handleShowToast("Cannot Delete Last KPI.", "info", true);
      setDeleteQuestionIndex({ sectionIndex: null, questionIndex: null });
    }
  };

  const validateForm = (): boolean => {
    // Tolerance for floating-point comparison
    const TOLERANCE = 0.01;

    // Validate form name
    if (!formData.form_name.trim()) {
      handleShowToast("Please enter a template name.", "error", true);
      return false;
    }

    // Validate section names and KPI questions
    for (let i = 0; i < formData.sections.length; i++) {
      const section = formData.sections[i];
      
      // Check if section name is empty
      if (!section.section.trim()) {
        handleShowToast(
          `Please enter a name for KRA Section ${i + 1}`,
          "error",
          true
        );
        return false;
      }

      // Check if each KPI question is filled
      for (let j = 0; j < section.questions.length; j++) {
        if (!section.questions[j].question.trim()) {
          handleShowToast(
            `Please enter a question for KPI ${j + 1} in "${section.section}"`,
            "error",
            true
          );
          return false;
        }
      }
    }

    // Validate total KRA weightage (with floating-point tolerance)
    const totalWeightage = calculateTotalWeightage();
    if (Math.abs(totalWeightage - 100) > TOLERANCE) {
      handleShowToast(
        `Total KRA weightage must equal 100%. Current total: ${totalWeightage.toFixed(2)}%`,
        "error",
        true
      );
      return false;
    }

    // Validate KPI weightages within each section (with floating-point tolerance)
    for (let i = 0; i < formData.sections.length; i++) {
      const sectionKPITotal = calculateSectionKPIWeightage(i);
      if (Math.abs(sectionKPITotal - 100) > TOLERANCE) {
        handleShowToast(
          `KPI weightages in "${formData.sections[i].section}" must total 100%. Current: ${sectionKPITotal.toFixed(2)}%`,
          "error",
          true
        );
        return false;
      }
    }

    return true;
  };

  const handleSaveDraft = async (): Promise<void> => {
    if (!formData.form_name.trim()) {
      handleShowToast("Please enter a template name.", "error", true);
      return;
    }

    // Get designation ID from URL params or location state
    const currentDesignationId = isEditMode ? location.state?.designationId : designationId;
    
    if (!currentDesignationId) {
      handleShowToast("Designation ID is missing. Please go back and select a designation.", "error", true);
      return;
    }

    // Transform data: rename 'questions' to 'kpis' for backend compatibility
    const transformedSections = formData.sections.map(section => ({
      section: section.section,
      weightage: section.weightage,
      kpis: section.questions.map(q => ({
        question: q.question,
        weightage: q.weightage,
        rating_type: q.rating_type || 'numerical',  // Include rating_type
      }))
    }));

    const data = {
      designation_id: currentDesignationId,
      form_name: formData.form_name,
      review_period: reviewPeriod,
      sections: transformedSections,
      is_draft: true,
    };

    try {
      if (isEditMode) {
        // Update existing template - use PUT method
        await dispatch(putData(updateKRAUrl(templateId!), data));
        handleShowToast("KRA Template updated successfully!", "success", true);
      } else {
        // Create new template
        await dispatch(postData(createKRAUrl, data));
        handleShowToast("KRA Template saved as draft successfully!", "success", true);
      }
      navigate("/pms/kra/library");
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorMessage = axiosError.response?.data?.error || axiosError.message || 
        (isEditMode ? "Failed to update template. Please try again." : "Failed to save draft. Please try again.");
      handleShowToast(errorMessage, "error", true);
    }
  };

  const handleCreateAndAssign = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    // Prevent double submission
    if (isSubmitting) return;

    if (!validateForm()) {
      return;
    }

    // Get designation ID from URL params or location state
    const currentDesignationId = isEditMode ? location.state?.designationId : designationId;
    
    if (!currentDesignationId) {
      handleShowToast("Designation ID is missing. Please go back and select a designation.", "error", true);
      return;
    }

    // Transform data: rename 'questions' to 'kpis' for backend compatibility
    const transformedSections = formData.sections.map(section => ({
      section: section.section,
      weightage: section.weightage,
      kpis: section.questions.map(q => ({
        question: q.question,
        weightage: q.weightage,
        rating_type: q.rating_type || 'numerical',  // Include rating_type
      }))
    }));

    const data = {
      designation_id: currentDesignationId,
      form_name: formData.form_name,
      review_period: reviewPeriod,
      enable_360_feedback: enable360Feedback,
      sections: transformedSections,
      is_draft: false,
    };

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      
      if (isEditMode) {
        // Update existing template - use PUT method
        await axios.put(updateKRAUrl(templateId!), data, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        handleShowToast("KRA Template updated successfully!", "success", true);
        
        // Navigate back to templates page
        navigate("/pms/kra/library");
      } else {
        // Create new template
        const response = await axios.post(createKRAUrl, data, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        
        const formId = response.data?.template_id;
        
        if (!formId) {
          throw new Error("Template ID not returned from server");
        }
        
        handleShowToast("KRA Template created successfully!", "success", true);
        
        // Navigate to assignment page
        navigate(`/pms/kra/assign/${formId}`, {
          state: { designationName, formData },
        });
      }
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorMessage = axiosError.response?.data?.error || axiosError.message || 
        (isEditMode ? "Failed to update template. Please try again." : "Failed to create template. Please try again.");
      handleShowToast(errorMessage, "error", true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 10, mb: 5 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(isEditMode ? "/pms/kra/library" : "/pms/designations")}
          sx={{ mb: 2 }}
        >
          {isEditMode ? "Back to Library" : "Back to Designations"}
        </Button>
        <Typography variant="h4" sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}>
          {isEditMode ? "Edit KRAs and KPIs" : "Create KRAs and KPIs"}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          For Role: <strong>{designationName}</strong>
        </Typography>
      </Box>

      {/* Template Name and Review Period */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="KRA Name"
            variant="outlined"
            fullWidth
            required
            placeholder="e.g., Software Engineer - Q1 2024"
            value={formData.form_name}
            onChange={(e) =>
              setFormData({ ...formData, form_name: e.target.value })
            }
          />
          <FormControl fullWidth required>
            <InputLabel>Review Period</InputLabel>
            <Select
              value={reviewPeriod}
              label="Review Period"
              onChange={(e) => setReviewPeriod(e.target.value)}
            >
              <MenuItem value="monthly">Monthly (1 Month)</MenuItem>
              <MenuItem value="quarterly">Quarterly (3 Months)</MenuItem>
              <MenuItem value="half_yearly">Half-Yearly (6 Months)</MenuItem>
              <MenuItem value="yearly">Yearly (12 Months)</MenuItem>
            </Select>
            <FormHelperText>Duration of this KRA review cycle</FormHelperText>
          </FormControl>
          
          {/* Enable 360° Feedback Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={enable360Feedback}
                onChange={(e) => setEnable360Feedback(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  Enable 360° Feedback
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Allow employees to submit yearly 360° feedback about their manager (Calendar year basis: Jan 1 - Dec 31)
                </Typography>
              </Box>
            }
          />
          
          {/* Warning when review period is changed and there are existing assignments */}
          {isEditMode && 
           templateData?.total_assignments !== undefined &&
           templateData.total_assignments > 0 && 
           reviewPeriod !== initialReviewPeriod && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: "600", mb: 0.5 }}>
                Review Period Changed
              </Typography>
              <Typography variant="body2">
                Changing the review period will automatically recalculate the end dates for{" "}
                <strong>{templateData?.total_assignments}</strong> existing assignment(s) 
                that are not yet completed. The assignment start dates will remain unchanged, 
                but the period duration will be updated to match the new review period.
              </Typography>
            </Alert>
          )}
        </Box>
      </Paper>

      {/* KRA/KPI Form */}
      <Box component="form" onSubmit={handleCreateAndAssign}>
        {/* Total KRA Weightage */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Total KRA Weightage: {calculateTotalWeightage().toFixed(0)}%
            </Typography>
            {calculateTotalWeightage() < 100 && (
              <Typography
                variant="body2"
                sx={{ color: "warning.main", fontWeight: "medium" }}
              >
                ⚠️ Remaining: {(100 - calculateTotalWeightage()).toFixed(1)}%
              </Typography>
            )}
            {calculateTotalWeightage() > 100 && (
              <Typography
                variant="body2"
                sx={{ color: "error.main", fontWeight: "medium" }}
              >
                ⚠️ Exceeds by {(calculateTotalWeightage() - 100).toFixed(1)}%
              </Typography>
            )}
            {calculateTotalWeightage() === 100 && (
              <Typography
                variant="body2"
                sx={{ color: "success.main", fontWeight: "medium" }}
              >
                ✅ Perfect!
              </Typography>
            )}
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={addSection}
          >
            Add KRA
          </Button>
        </Box>

        {/* KRA Sections */}
        <Box>
          {formData.sections.map((section, sectionIndex) => (
            <Card
              key={sectionIndex}
              elevation={3}
              sx={{
                mb: 3,
                p: 2,
                border: "1px solid",
                borderColor: "grey.300",
              }}
            >
              <Box sx={{ m: 2, p: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 1 }}>
                    <InputLabel
                      sx={{ fontWeight: "bold", flexShrink: 0, minWidth: "fit-content" }}
                    >
                      KRA {sectionIndex + 1} :
                    </InputLabel>
                    <TextField
                      variant="standard"
                      fullWidth
                      multiline
                      maxRows={4}
                      required
                      placeholder="Enter KRA Name (e.g., 'Operational Efficiency', 'Revenue Growth')"
                      value={section.section}
                      onChange={(e) => {
                        const updatedFormData = { ...formData };
                        updatedFormData.sections[sectionIndex].section = e.target.value;
                        setFormData(updatedFormData);
                      }}
                    />
                  </Box>

                  {/* KRA Weightage */}
                  <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, gap: 1 }}>
                    <InputLabel sx={{ fontWeight: "bold", flexShrink: 0 }}>
                      Weightage :
                    </InputLabel>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const updatedFormData = { ...formData };
                          const currentValue = updatedFormData.sections[sectionIndex].weightage || 0;
                          updatedFormData.sections[sectionIndex].weightage = Math.max(0, currentValue - 5);
                          setFormData(updatedFormData);
                        }}
                        sx={{
                          border: "1px solid",
                          borderColor: "grey.300",
                          borderRadius: "4px",
                          width: "24px",
                          height: "24px",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "16px", fontWeight: "bold" }}>
                          −
                        </Typography>
                      </IconButton>
                      <TextField
                        variant="standard"
                        type="number"
                        inputProps={{ min: 0, max: 100, step: 0.1, style: { textAlign: "center" } }}
                        sx={{ width: "80px", flexShrink: 0 }}
                        required
                        value={section.weightage === 0 ? "" : section.weightage}
                        placeholder="0%"
                        onChange={(e) => {
                          const updatedFormData = { ...formData };
                          const value = e.target.value;
                          updatedFormData.sections[sectionIndex].weightage =
                            value === "" ? 0 : parseFloat(value) || 0;
                          setFormData(updatedFormData);
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const updatedFormData = { ...formData };
                          const currentValue = updatedFormData.sections[sectionIndex].weightage || 0;
                          updatedFormData.sections[sectionIndex].weightage = Math.min(100, currentValue + 5);
                          setFormData(updatedFormData);
                        }}
                        sx={{
                          border: "1px solid",
                          borderColor: "grey.300",
                          borderRadius: "4px",
                          width: "24px",
                          height: "24px",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "16px", fontWeight: "bold" }}>
                          +
                        </Typography>
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Delete Section Icon */}
                  <IconButton
                    onClick={() => setDeleteSectionIndex(sectionIndex)}
                    sx={{
                      flexShrink: 0,
                      ml: 1,
                      color: "grey.400",
                      "&:hover": {
                        color: "error.main",
                        backgroundColor: "error.lighter",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </Box>
              </Box>

              {/* KPIs */}
              {section.questions.map((question, questionIndex) => (
                <Box
                  sx={{ m: 4, ml: 6, pl: 3, borderLeft: "3px solid", borderColor: "grey.300" }}
                  key={questionIndex}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 1 }}>
                      <InputLabel sx={{ fontWeight: "bold", flexShrink: 0 }}>
                        KPI {questionIndex + 1} :
                      </InputLabel>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        maxRows={4}
                        value={question.question}
                        required
                        placeholder="Enter KPI Name (e.g., 'Reduce operational costs by 10%')"
                        variant="standard"
                        onChange={(e) => {
                          const updatedFormData = { ...formData };
                          updatedFormData.sections[sectionIndex].questions[questionIndex].question =
                            e.target.value;
                          setFormData(updatedFormData);
                        }}
                      />
                    </Box>

                    {/* KPI Weightage */}
                    <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, gap: 1 }}>
                      <InputLabel sx={{ fontWeight: "bold", flexShrink: 0 }}>
                        Weightage :
                      </InputLabel>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const updatedFormData = { ...formData };
                            const currentValue =
                              updatedFormData.sections[sectionIndex].questions[questionIndex].weightage || 0;
                            updatedFormData.sections[sectionIndex].questions[questionIndex].weightage = Math.max(
                              0,
                              currentValue - 5
                            );
                            setFormData(updatedFormData);
                          }}
                          sx={{
                            border: "1px solid",
                            borderColor: "grey.300",
                            borderRadius: "4px",
                            width: "24px",
                            height: "24px",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: "16px", fontWeight: "bold" }}>
                            −
                          </Typography>
                        </IconButton>
                        <TextField
                          variant="standard"
                          type="number"
                          inputProps={{ min: 0, max: 100, step: 0.1, style: { textAlign: "center" } }}
                          sx={{ width: "80px", flexShrink: 0 }}
                          required
                          value={question.weightage === 0 ? "" : question.weightage}
                          placeholder="0%"
                          onChange={(e) => {
                            const updatedFormData = { ...formData };
                            const value = e.target.value;
                            updatedFormData.sections[sectionIndex].questions[questionIndex].weightage =
                              value === "" ? 0 : parseFloat(value) || 0;
                            setFormData(updatedFormData);
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            const updatedFormData = { ...formData };
                            const currentValue =
                              updatedFormData.sections[sectionIndex].questions[questionIndex].weightage || 0;
                            updatedFormData.sections[sectionIndex].questions[questionIndex].weightage = Math.min(
                              100,
                              currentValue + 5
                            );
                            setFormData(updatedFormData);
                          }}
                          sx={{
                            border: "1px solid",
                            borderColor: "grey.300",
                            borderRadius: "4px",
                            width: "24px",
                            height: "24px",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: "16px", fontWeight: "bold" }}>
                            +
                          </Typography>
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Delete KPI Icon */}
                    <IconButton
                      onClick={() => setDeleteQuestionIndex({ sectionIndex, questionIndex })}
                      sx={{
                        flexShrink: 0,
                        ml: 1,
                        color: "grey.400",
                        "&:hover": {
                          color: "error.main",
                          backgroundColor: "error.lighter",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="medium" />
                    </IconButton>
                  </Box>

                  {/* Rating Type Selector */}
                  <Box sx={{ mt: 1.5, mb: 0.5 }}>
                    <FormControl size="small" sx={{ minWidth: 250 }}>
                      <InputLabel>Rating Type</InputLabel>
                      <Select
                        value={question.rating_type || 'numerical'}
                        label="Rating Type"
                        onChange={(e) => {
                          const updatedFormData = { ...formData };
                          updatedFormData.sections[sectionIndex].questions[questionIndex].rating_type = 
                            e.target.value as 'numerical' | 'likert' | 'boolean';
                          setFormData(updatedFormData);
                        }}
                        renderValue={(value) => {
                          if (value === 'likert') {
                            return 'Likert - 1-5';
                          }
                          if (value === 'numerical') {
                            return 'Numerical - 0-5';
                          }
                          if (value === 'boolean') {
                            return 'Boolean - Yes/No';
                          }
                          return value;
                        }}
                      >
                        <MenuItem value="numerical">Numerical - 0-5</MenuItem>
                        <MenuItem value="likert">Likert - 5-Outstanding / 4-Exceeds Expectations / 3-Meets Expectations / 2-Needs Improvement / 1-Unacceptable</MenuItem>
                        <MenuItem value="boolean">Boolean - Yes/No</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Section KPI Total Display */}
                  {questionIndex === section.questions.length - 1 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2, ml: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        Total KPI Weightage: {calculateSectionKPIWeightage(sectionIndex).toFixed(0)}%
                      </Typography>
                      {calculateSectionKPIWeightage(sectionIndex) < 100 && (
                        <Typography variant="body2" sx={{ color: "warning.main", fontWeight: "medium" }}>
                          ⚠️ Remaining: {(100 - calculateSectionKPIWeightage(sectionIndex)).toFixed(1)}%
                        </Typography>
                      )}
                      {calculateSectionKPIWeightage(sectionIndex) > 100 && (
                        <Typography variant="body2" sx={{ color: "error.main", fontWeight: "medium" }}>
                          ⚠️ Exceeds by {(calculateSectionKPIWeightage(sectionIndex) - 100).toFixed(1)}%
                        </Typography>
                      )}
                      {calculateSectionKPIWeightage(sectionIndex) === 100 && (
                        <Typography variant="body2" sx={{ color: "success.main", fontWeight: "medium" }}>
                          ✅ Perfect!
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              ))}

              <Box sx={{ display: "flex", justifyContent: "right", m: 1 }}>
                <Button variant="outlined" onClick={() => addQuestion(sectionIndex)} size="small">
                  Add KPI
                </Button>
              </Box>
            </Card>
          ))}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
          {!isEditMode && (
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSaveDraft}
              size="large"
            >
              Save as Draft
            </Button>
          )}
          <Button
            variant="contained"
            type="submit"
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            disabled={isSubmitting}
            size="large"
          >
            {isSubmitting 
              ? (isEditMode ? "Updating..." : "Creating...") 
              : (isEditMode ? "Update" : "Create & Assign")
            }
          </Button>
        </Box>
      </Box>

      {/* Delete Section Dialog */}
      <Dialog open={deleteSectionIndex !== null} onClose={() => setDeleteSectionIndex(null)}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end" }}>
          <IconButton onClick={() => setDeleteSectionIndex(null)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <Typography>Do you want to delete this KRA?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSectionIndex(null)}>No</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (deleteSectionIndex !== null) {
                deleteSection(deleteSectionIndex);
              }
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Question Dialog */}
      <Dialog
        open={
          deleteQuestionIndex.sectionIndex !== null && deleteQuestionIndex.questionIndex !== null
        }
        onClose={() => setDeleteQuestionIndex({ sectionIndex: null, questionIndex: null })}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end" }}>
          <IconButton onClick={() => setDeleteQuestionIndex({ sectionIndex: null, questionIndex: null })}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <Typography>Do you want to delete this KPI?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteQuestionIndex({ sectionIndex: null, questionIndex: null })}>
            No
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (
                deleteQuestionIndex.sectionIndex !== null &&
                deleteQuestionIndex.questionIndex !== null
              ) {
                deleteQuestion(deleteQuestionIndex.sectionIndex, deleteQuestionIndex.questionIndex);
              }
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreateKRAForm;
